import { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Button } from '@antimatterhq/ui/components/button';
import { core_v1_Rule } from '@antimatterhq/asl-api-ts-v1';
import { useDeleteRule } from '@/hooks/core/useDeleteRule';
import { useGetRulesInfinite } from '@/hooks/core/useGetRulesInfinite';

interface DeleteDetectionRulesDialogProps {
  rule?: core_v1_Rule;
  onClose: () => void;
  'data-testid'?: string;
}

export function DeleteDetectionRulesDialog(
  props: DeleteDetectionRulesDialogProps,
) {
  const testId = props['data-testid'] || 'delete-detection-rule-dialog';
  const { mutateAsync: deleteRuleAsync, isPending } = useDeleteRule();
  const { optimisticDelete } = useGetRulesInfinite({ limit: 500 });

  const onOpenChange = (open: boolean) => {
    if (!open) {
      props.onClose();
    }
  };

  const handleDeleteRule = useCallback(async () => {
    if (!props.rule?.metadata?.name) return;

    await deleteRuleAsync(
      { name: props.rule.metadata?.name },
      {
        toasts: {
          success: `${props.rule.metadata?.name} deleted successfully`,
          error: (error) =>
            `Failed to delete ${props.rule?.metadata?.name}. "${error.response?.statusText}"`,
        },
        onSuccess: async () => {
          optimisticDelete(props.rule!);
          props.onClose();
        },
        onError: async ({ error }) => {
          // Not useful in this case, but ideally can be used to update form errors
          console.error(error);
        },
      },
    );
  }, [props, deleteRuleAsync, optimisticDelete]);

  return (
    <Dialog open={props.rule !== undefined} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-full flex-col" data-testid={testId}>
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>Delete {props.rule?.metadata?.name || ''}?</DialogTitle>
        </DialogHeader>
        <div className="flex w-full justify-end gap-4">
          <Button
            onClick={props.onClose}
            variant={'ghost'}
            disabled={isPending}
            data-testid={`${testId}-cancel-button`}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRule}
            variant={'danger'}
            disabled={isPending}
            data-testid={`${testId}-delete-button`}
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
